import { haversineKm } from '../../../utils/roadDistance';
import { toLandmass } from '../../bookings/lib/crossing';
import type { QueueRow } from './queue';
import temperatureBandsData from './temperatureBands.json';
import configData from './suggestionConfig.json';

// ── Temperature bands ───────────────────────────────────────────────────────

export type TempBand = 'Frozen' | 'Chilled' | 'Warm' | 'Ambient';

interface TemperatureBand {
  type: TempBand;
  minTemp: number | null;
  maxTemp: number | null;
}

const TEMP_BANDS = temperatureBandsData.temperatureBands as TemperatureBand[];

/**
 * Map a set point (°C) to its band. `null` (no temperature requirement) is
 * Ambient. Matched by ascending max so boundary values land in the lower band;
 * anything above the warmest range falls back to Warm.
 */
export function tempBandFor(setPointC: number | null | undefined): TempBand {
  if (setPointC == null) return 'Ambient';
  const ranged = TEMP_BANDS.filter((b) => b.maxTemp != null).sort(
    (a, b) => (a.maxTemp as number) - (b.maxTemp as number),
  );
  for (const b of ranged) if (setPointC <= (b.maxTemp as number)) return b.type;
  return 'Warm';
}

// ── Config (editable via suggestionConfig.json) ─────────────────────────────

interface SuggestionConfig {
  /** A lone Same-Location task surfaces only at/above this fill of capacity. */
  surfaceSinglePct: number;
  /** Fill at/above which a load is "Near full". */
  nearFullPct: number;
  furthest: {
    /** Anchor only on deliveries at least this far from the depot (else → Clusters). */
    minAnchorKm: number;
    /** Catchment circle around the anchor. */
    anchorRadiusKm: number;
    /** Half-width of the depot→anchor corridor band. */
    corridorHalfWidthKm: number;
  };
  clusters: {
    /** Tight grouping radius. */
    radiusKm: number;
  };
  /**
   * Post-pass: merge under-filled loads whose centroids sit within this many km
   * of each other (same country, ≤ 2 temp bands, still fitting one trailer) so
   * the truck runs as full as proximity allows. 0 disables the pass.
   */
  consolidationRadiusKm: number;
  /** Country code → group. Codes not listed map to themselves. NI(XI)+ROI(IE)=IE. */
  countryGroups: Record<string, string>;
}

export const SUGGESTION_CONFIG = configData as SuggestionConfig;

/** Friendly country labels for International cards; falls back to the code. */
const COUNTRY_NAMES: Record<string, string> = {
  IE: 'Ireland',
  GB: 'Great Britain',
  FR: 'France',
  DE: 'Germany',
  NL: 'Netherlands',
  BE: 'Belgium',
  ES: 'Spain',
};

// ── Output shape ────────────────────────────────────────────────────────────

export type SuggestionType = 'sameLocation' | 'international' | 'furthest' | 'clusters';
export type Confidence = 'high' | 'medium' | 'low';

export interface SuggestedLoad {
  id: string;
  type: SuggestionType;
  title: string;
  /** Display label for the temperature(s): a band name, or "A + B" for a split trailer. */
  tempProfile: string;
  /** Distinct temp bands on this load (1, or 2 for a split-compartment trailer). */
  bands: TempBand[];
  /** The grouped queue rows — used to build the load + render the card body. */
  rows: QueueRow[];
  totalLm: number;
  capacityLm: number;
  fillPct: number;
  /** Distinct stops (addresses) in the load. */
  stops: number;
  /**
   * Approximate one-way reach from the depot, km. Furthest uses its anchor
   * distance and Clusters its centroid distance; everything else falls back to
   * the depot→furthest-stop estimate. Undefined only when the depot has no coords.
   */
  distanceKm?: number;
  badge: 'full' | 'nearFull' | null;
  confidence: Confidence;
}

export interface SuggestDepot {
  lat: number | null;
  lng: number | null;
  country: string | null;
}

// ── Geometry / helpers ──────────────────────────────────────────────────────

const lmOf = (r: QueueRow) => r.lm ?? 0;
const sumLm = (rows: QueueRow[]) => rows.reduce((s, r) => s + lmOf(r), 0);
const round2 = (n: number) => Math.round(n * 100) / 100;
const addrKey = (r: QueueRow) => r.addressId ?? `${r.lat ?? '?'},${r.lng ?? '?'}`;

function normCountry(code: string | null | undefined): string {
  if (!code) return '??';
  const up = code.toUpperCase();
  return SUGGESTION_CONFIG.countryGroups[up] ?? up;
}

const stopCountry = (r: QueueRow) =>
  normCountry(r.role === 'collection' ? r.collectionCountry : r.deliveryCountry);
const stopCity = (r: QueueRow) =>
  (r.role === 'collection' ? r.collectionCity : r.deliveryCity) || '—';
const stopAddress = (r: QueueRow) =>
  (r.role === 'collection' ? r.collectionAddress : r.deliveryAddress) || '—';

/** Depot → furthest stop in the load, km — a generic one-way reach estimate. */
function reachKm(depot: { lat: number; lng: number }, rows: QueueRow[]): number | undefined {
  let max = -1;
  for (const r of rows) {
    if (r.lat == null || r.lng == null) continue;
    const d = haversineKm(depot.lat, depot.lng, r.lat, r.lng);
    if (d > max) max = d;
  }
  return max >= 0 ? round2(max) : undefined;
}

function toLocalKm(originLat: number, originLng: number, lat: number, lng: number) {
  const x = (lng - originLng) * Math.cos((originLat * Math.PI) / 180) * 111.32;
  const y = (lat - originLat) * 110.57;
  return { x, y };
}

/** Is `p` within the depot→anchor corridor band (and between the two endpoints)? */
function onCorridor(
  depot: { lat: number; lng: number },
  anchor: { lat: number; lng: number },
  p: { lat: number; lng: number },
): boolean {
  const a = toLocalKm(depot.lat, depot.lng, anchor.lat, anchor.lng);
  const q = toLocalKm(depot.lat, depot.lng, p.lat, p.lng);
  const len2 = a.x * a.x + a.y * a.y;
  if (len2 === 0) return false;
  const t = (q.x * a.x + q.y * a.y) / len2;
  if (t < 0 || t > 1) return false;
  const cross = Math.abs(q.x * a.y - q.y * a.x) / Math.sqrt(len2);
  return cross <= SUGGESTION_CONFIG.furthest.corridorHalfWidthKm;
}

// ── Temperature bands ───────────────────────────────────────────────────────
// A trailer has up to TWO compartments, so a load may carry up to two temp
// bands (any pair — e.g. Ambient + Frozen). The packers below enforce that cap.

const TEMP_ORDER: TempBand[] = ['Frozen', 'Chilled', 'Warm', 'Ambient'];
const MAX_BANDS_PER_LOAD = 2;

/** Distinct bands on a set of rows, in canonical order. */
function bandsOf(rows: QueueRow[]): TempBand[] {
  const present = new Set(rows.map((r) => tempBandFor(r.setPointC)));
  return TEMP_ORDER.filter((b) => present.has(b));
}

/**
 * Adding `r` keeps the load within capacity, on one landmass, AND within 2
 * distinct temp bands.
 *
 * <p>
 * The single gate for "may this row join this load", so the landmass test lives
 * here as well as in the radius clustering — `packByProximity` adds rows by
 * distance alone, and distance across the Irish Sea is a lie.
 * </p>
 */
function canAdd(load: QueueRow[], lm: number, r: QueueRow, cap: number): boolean {
  if (lm + lmOf(r) > cap) return false;
  if (!load.every((x) => sameLandmass(x, r))) return false;
  const band = tempBandFor(r.setPointC);
  const bands = new Set(load.map((x) => tempBandFor(x.setPointC)));
  return bands.has(band) || bands.size < MAX_BANDS_PER_LOAD;
}

// ── Capacity packing (band-aware: ≤ 2 temp bands per load) ──────────────────

/** First-fit-decreasing by LM — for groups where distance doesn't matter. */
function packByLm(rows: QueueRow[], cap: number): QueueRow[][] {
  const sorted = [...rows].sort((a, b) => lmOf(b) - lmOf(a));
  const loads: QueueRow[][] = [];
  for (const r of sorted) {
    const fit = loads.find((l) => canAdd(l, sumLm(l), r, cap));
    if (fit) fit.push(r);
    else loads.push([r]);
  }
  return loads;
}

/**
 * Group rows that sit within `radiusKm` of a seed (seed-and-absorb, same as the
 * Clusters strategy). Rows without coordinates can't be placed geographically,
 * so they're returned as a single trailing group.
 */
/**
 * The landmass a row's own stop sits on — its delivery for a drop, its
 * collection for a pickup.
 */
function rowLandmass(row: QueueRow): string | null {
  return toLandmass(row.role === 'collection' ? row.collectionCountry : row.deliveryCountry);
}

/**
 * Can these two share a load on geography alone?
 *
 * <p>
 * STRAIGHT-LINE DISTANCE LIES ACROSS WATER, and this is where it bites: Cork
 * to Swansea is 250 km as the crow flies and a ferry apart by road, so a radius
 * that legitimately groups two Cork drops will just as happily group a Welsh
 * one. Rejecting the pair on landmass is nearly free; discovering it by routing
 * every candidate pair is thousands of metered calls per screen.
 * </p>
 * <p>
 * Unknown countries pass: half the address book would otherwise stop clustering
 * because a country code was never filled in.
 * </p>
 */
function sameLandmass(a: QueueRow, b: QueueRow): boolean {
  const la = rowLandmass(a);
  const lb = rowLandmass(b);
  return la === null || lb === null || la === lb;
}

function clusterByRadius(rows: QueueRow[], radiusKm: number): QueueRow[][] {
  const pool = rows.filter((r) => r.lat != null && r.lng != null);
  const flat = rows.filter((r) => r.lat == null || r.lng == null);
  const groups: QueueRow[][] = [];
  while (pool.length) {
    const seed = pool.shift() as QueueRow;
    const group = [seed];
    for (let j = pool.length - 1; j >= 0; j--) {
      const r = pool[j] as QueueRow;
      if (!sameLandmass(seed, r)) continue;
      if (haversineKm(seed.lat as number, seed.lng as number, r.lat as number, r.lng as number) <= radiusKm) {
        group.push(r);
        pool.splice(j, 1);
      }
    }
    groups.push(group);
  }
  if (flat.length) groups.push(flat);
  return groups;
}

/** Pack greedily by proximity — seed a load, then add the nearest rows that fit. */
function packByProximity(rows: QueueRow[], cap: number): QueueRow[][] {
  const remaining = [...rows];
  const loads: QueueRow[][] = [];
  while (remaining.length) {
    const seed = remaining.shift() as QueueRow;
    const load = [seed];
    let lm = lmOf(seed);
    for (;;) {
      let bestIdx = -1;
      let bestD = Infinity;
      for (let i = 0; i < remaining.length; i++) {
        const r = remaining[i];
        if (!r || !canAdd(load, lm, r, cap)) continue;
        const d =
          seed.lat != null && seed.lng != null && r.lat != null && r.lng != null
            ? haversineKm(seed.lat, seed.lng, r.lat, r.lng)
            : 0;
        if (d < bestD) {
          bestD = d;
          bestIdx = i;
        }
      }
      if (bestIdx < 0) break;
      const picked = remaining.splice(bestIdx, 1)[0] as QueueRow;
      load.push(picked);
      lm += lmOf(picked);
    }
    loads.push(load);
  }
  return loads;
}

function makeLoad(
  type: SuggestionType,
  id: string,
  title: string,
  rows: QueueRow[],
  cap: number,
  confidence: Confidence,
  distanceKm?: number,
): SuggestedLoad {
  const totalLm = round2(sumLm(rows));
  const fillPct = cap > 0 ? totalLm / cap : 0;
  const badge: SuggestedLoad['badge'] =
    fillPct >= 1 ? 'full' : fillPct >= SUGGESTION_CONFIG.nearFullPct ? 'nearFull' : null;
  const stops = new Set(rows.map(addrKey)).size;
  const bands = bandsOf(rows);
  const tempProfile = bands.length <= 1 ? (bands[0] ?? 'Ambient') : bands.join(' + ');
  return {
    id,
    type,
    title,
    tempProfile,
    bands,
    rows,
    totalLm,
    capacityLm: round2(cap),
    fillPct: round2(fillPct),
    stops,
    distanceKm,
    badge,
    confidence,
  };
}

/** Remove rows claimed by the given loads from the pool (waterfall step). */
function removeClaimed(pool: QueueRow[], loads: SuggestedLoad[]): QueueRow[] {
  const claimed = new Set<string>();
  for (const l of loads) for (const r of l.rows) claimed.add(r.itemId);
  return pool.filter((r) => !claimed.has(r.itemId));
}

// ── Strategies ──────────────────────────────────────────────────────────────

/** Same physical address (sub-metre) — group by the leg's address id. */
function sameLocation(rows: QueueRow[], depotCountry: string, cap: number): SuggestedLoad[] {
  const byAddress = new Map<string, QueueRow[]>();
  for (const r of rows) {
    // A stop in a different country than the depot is always International,
    // never Same Location (unknown-country stops still group here). IE/XI are
    // already collapsed to one country by normCountry.
    const c = stopCountry(r);
    if (c !== '??' && c !== depotCountry) continue;
    const key = addrKey(r);
    (byAddress.get(key) ?? byAddress.set(key, []).get(key)!).push(r);
  }
  const out: SuggestedLoad[] = [];
  let i = 0;
  for (const group of byAddress.values()) {
    for (const load of packByLm(group, cap)) {
      const surfaced =
        load.length >= 2 || sumLm(load) >= cap * SUGGESTION_CONFIG.surfaceSinglePct;
      if (!surfaced) continue;
      out.push(
        makeLoad('sameLocation', `sameLocation-${i++}`, stopAddress(load[0] as QueueRow), load, cap, 'high'),
      );
    }
  }
  return out.sort((a, b) => b.fillPct - a.fillPct);
}

/** Foreign destinations — one country per group, packed by proximity within. */
function international(rows: QueueRow[], depotCountry: string, cap: number): SuggestedLoad[] {
  const byCountry = new Map<string, QueueRow[]>();
  for (const r of rows) {
    const c = stopCountry(r);
    if (c === '??' || c === depotCountry) continue;
    (byCountry.get(c) ?? byCountry.set(c, []).get(c)!).push(r);
  }
  const out: SuggestedLoad[] = [];
  let i = 0;
  for (const [country, group] of byCountry) {
    const label = COUNTRY_NAMES[country] ?? country;
    // Cluster within the country first so far-apart drops (e.g. Andover ↔
    // Warton, ~400 km) form separate loads; the consolidation pass later fills
    // nearby clusters up to capacity.
    for (const cluster of clusterByRadius(group, SUGGESTION_CONFIG.clusters.radiusKm)) {
      for (const load of packByLm(cluster, cap)) {
        const city = areaTitle(load);
        const title = city === 'Multiple stops' ? label : `${label} · ${city}`;
        out.push(makeLoad('international', `international-${i++}`, title, load, cap, 'medium'));
      }
    }
  }
  return out.sort((a, b) => b.fillPct - a.fillPct);
}

/** Domestic deliveries — greedy furthest-first corridors (anchor must be far). */
function furthest(
  rows: QueueRow[],
  depot: { lat: number; lng: number },
  depotCountry: string,
  cap: number,
): SuggestedLoad[] {
  const pool = rows.filter(
    (r) => r.role === 'delivery' && stopCountry(r) === depotCountry && r.lat != null && r.lng != null,
  );
  const out: SuggestedLoad[] = [];
  let i = 0;
  while (pool.length) {
    let anchor = pool[0] as QueueRow;
    let anchorD = -1;
    for (const r of pool) {
      const d = haversineKm(depot.lat, depot.lng, r.lat as number, r.lng as number);
      if (d > anchorD) {
        anchorD = d;
        anchor = r;
      }
    }
    // The furthest remaining is no longer "far" — leave the rest for Clusters.
    if (anchorD < SUGGESTION_CONFIG.furthest.minAnchorKm) break;

    const aPt = { lat: anchor.lat as number, lng: anchor.lng as number };
    const claimed: QueueRow[] = [];
    for (let j = pool.length - 1; j >= 0; j--) {
      const r = pool[j] as QueueRow;
      const p = { lat: r.lat as number, lng: r.lng as number };
      const near = haversineKm(aPt.lat, aPt.lng, p.lat, p.lng) <= SUGGESTION_CONFIG.furthest.anchorRadiusKm;
      if (r === anchor || near || onCorridor(depot, aPt, p)) {
        claimed.push(r);
        pool.splice(j, 1);
      }
    }

    const title = stopCity(anchor);
    for (const load of packByProximity(claimed, cap)) {
      out.push(makeLoad('furthest', `furthest-${i++}`, title, load, cap, 'medium', anchorD));
    }
  }
  return out.sort((a, b) => b.fillPct - a.fillPct);
}

/** Tight local clusters of remaining domestic deliveries. */
function clusters(
  rows: QueueRow[],
  depot: { lat: number; lng: number },
  depotCountry: string,
  cap: number,
): SuggestedLoad[] {
  const pool = rows.filter(
    (r) => r.role === 'delivery' && stopCountry(r) === depotCountry && r.lat != null && r.lng != null,
  );
  const radius = SUGGESTION_CONFIG.clusters.radiusKm;
  const out: SuggestedLoad[] = [];
  let i = 0;
  while (pool.length) {
    const seed = pool.shift() as QueueRow;
    const group = [seed];
    for (let j = pool.length - 1; j >= 0; j--) {
      const r = pool[j] as QueueRow;
      if (haversineKm(seed.lat as number, seed.lng as number, r.lat as number, r.lng as number) <= radius) {
        group.push(r);
        pool.splice(j, 1);
      }
    }
    // Centroid + corridor length, and an address-level title (nearest the centroid).
    const cLat = group.reduce((s, r) => s + (r.lat as number), 0) / group.length;
    const cLng = group.reduce((s, r) => s + (r.lng as number), 0) / group.length;
    const distanceKm = haversineKm(depot.lat, depot.lng, cLat, cLng);
    let centreRow = group[0] as QueueRow;
    let centreD = Infinity;
    for (const r of group) {
      const d = haversineKm(cLat, cLng, r.lat as number, r.lng as number);
      if (d < centreD) {
        centreD = d;
        centreRow = r;
      }
    }
    const title = stopAddress(centreRow);

    for (const load of packByProximity(group, cap)) {
      const fill = cap > 0 ? sumLm(load) / cap : 0;
      const confidence: Confidence = fill >= SUGGESTION_CONFIG.nearFullPct ? 'high' : 'low';
      out.push(makeLoad('clusters', `clusters-${i++}`, title, load, cap, confidence, distanceKm));
    }
  }
  return out.sort((a, b) => b.fillPct - a.fillPct);
}

// ── Consolidation — merge nearby under-filled loads to maximise truck fill ──
// Each strategy groups by its own rule, which can still leave several half-empty
// loads in the same area (e.g. two Dublin drops a few km apart, each ~30% full).
// This post-pass greedily merges loads that are in the same country, sit within
// `consolidationRadiusKm` of each other, share ≤ 2 temp bands and still fit one
// trailer — so the truck runs as full as proximity allows. Far-apart loads
// (e.g. Andover ↔ Warton, ~400 km) stay separate because they exceed the radius.

/** Average lat/lng of a load's geocoded rows, or null when none have coords. */
function centroidOf(rows: QueueRow[]): { lat: number; lng: number } | null {
  let lat = 0;
  let lng = 0;
  let n = 0;
  for (const r of rows) {
    if (r.lat == null || r.lng == null) continue;
    lat += r.lat;
    lng += r.lng;
    n++;
  }
  return n ? { lat: lat / n, lng: lng / n } : null;
}

/** Short label for a merged load: up to two distinct cities, then "+N". */
function areaTitle(rows: QueueRow[]): string {
  const cities: string[] = [];
  for (const r of rows) {
    const c = stopCity(r);
    if (c !== '—' && !cities.includes(c)) cities.push(c);
  }
  if (cities.length === 0) return 'Multiple stops';
  if (cities.length <= 2) return cities.join(' / ');
  return `${cities[0]} +${cities.length - 1}`;
}

const CONF_RANK: Record<Confidence, number> = { low: 0, medium: 1, high: 2 };
const RANK_CONF: Confidence[] = ['low', 'medium', 'high'];

function consolidate(
  loads: SuggestedLoad[],
  cap: number,
  depotPt: { lat: number; lng: number } | null,
): SuggestedLoad[] {
  const radius = SUGGESTION_CONFIG.consolidationRadiusKm;
  if (radius <= 0 || loads.length < 2) return loads;

  const result = [...loads];
  let mergedCount = 0;
  for (;;) {
    // Pick the fullest feasible pair (tie-break: closest) and merge — repeat
    // until nothing else can be combined within the radius/capacity rules.
    let bestI = -1;
    let bestJ = -1;
    let bestLm = -1;
    let bestD = Infinity;
    for (let i = 0; i < result.length; i++) {
      const a = result[i]!;
      const aC = centroidOf(a.rows);
      if (!aC) continue;
      const aCountry = stopCountry(a.rows[0]!);
      for (let j = i + 1; j < result.length; j++) {
        const b = result[j]!;
        if (stopCountry(b.rows[0]!) !== aCountry) continue;
        const combinedLm = sumLm(a.rows) + sumLm(b.rows);
        if (combinedLm > cap) continue;
        if (bandsOf([...a.rows, ...b.rows]).length > MAX_BANDS_PER_LOAD) continue;
        const bC = centroidOf(b.rows);
        if (!bC) continue;
        const d = haversineKm(aC.lat, aC.lng, bC.lat, bC.lng);
        if (d > radius) continue;
        if (combinedLm > bestLm || (combinedLm === bestLm && d < bestD)) {
          bestLm = combinedLm;
          bestD = d;
          bestI = i;
          bestJ = j;
        }
      }
    }
    if (bestI < 0) break;

    const a = result[bestI]!;
    const b = result[bestJ]!;
    const rows = [...a.rows, ...b.rows];
    // The bigger contributor keeps its strategy type (so the merged load stays
    // in the same filter the user found it under) and confidence is the weaker.
    const primary = sumLm(a.rows) >= sumLm(b.rows) ? a : b;
    const confidence = RANK_CONF[Math.min(CONF_RANK[a.confidence], CONF_RANK[b.confidence])]!;
    const distanceKm = depotPt
      ? reachKm(depotPt, rows)
      : Math.max(a.distanceKm ?? 0, b.distanceKm ?? 0) || undefined;
    result[bestI] = makeLoad(primary.type, `merged-${mergedCount++}`, areaTitle(rows), rows, cap, confidence, distanceKm);
    result.splice(bestJ, 1);
  }
  return result;
}

// ── Entry point — waterfall: each stage claims its rows, the rest flow down ──

export function suggestLoads(args: {
  rows: QueueRow[];
  depot: SuggestDepot;
  capacityLm: number;
}): SuggestedLoad[] {
  const cap = args.capacityLm > 0 ? args.capacityLm : 13.4;
  const depotCountry = normCountry(args.depot.country);
  const depotPt =
    args.depot.lat != null && args.depot.lng != null
      ? { lat: args.depot.lat, lng: args.depot.lng }
      : null;
  const out: SuggestedLoad[] = [];
  let pool = [...args.rows];

  const sl = sameLocation(pool, depotCountry, cap);
  out.push(...sl);
  pool = removeClaimed(pool, sl);

  const intl = international(pool, depotCountry, cap);
  out.push(...intl);
  pool = removeClaimed(pool, intl);

  if (depotPt) {
    const fr = furthest(pool, depotPt, depotCountry, cap);
    out.push(...fr);
    pool = removeClaimed(pool, fr);

    const cl = clusters(pool, depotPt, depotCountry, cap);
    out.push(...cl);

    // Fill in a reach estimate for strategies that don't compute their own
    // (Same Location, International) so every card shows a distance.
    for (const l of out) {
      if (l.distanceKm == null) l.distanceKm = reachKm(depotPt, l.rows);
    }
  }

  // Final pass: combine nearby under-filled loads to maximise truck fill.
  return consolidate(out, cap, depotPt);
}
